import React, {useState, useEffect} from 'react';
import {BaseSettingsForm, SelectField} from '@/components/forms';
import {Form, Col, Row, Card} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {useStoreActions, useStoreState} from 'easy-peasy';
import {useAlert} from '@/components/alerts';
import SearchSelect from '@/components/search_select/SearchSelect';
import useData from '@/utils/useData';
import {buildNotificationURL} from '@/utils/urlBuilder';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import {MultiValue} from 'react-select';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import * as yup from 'yup';
import i18n from 'i18n';

interface ScheduleChoice {
    value: string;
    label: string;
}

interface UserOption {
    value: number | string;
    label: string;
}

interface User {
    id: number;
    identifier: string;
    email: string;
}

interface FormValues {
    title: string;
    schedule: string;
    storyboardId: number;
    active: boolean;
}

interface NotificationsSettingsFormProps {
    notificationId?: number;
    onDone: () => void;
}

const notificationSchema = yup.object({
    title: yup.string().required(i18n.t('jt_notification_title_required.text')),
    schedule: yup.string().required(i18n.t('jt_notification_schedule_required.text')),
    active: yup.boolean(),
    storyboardId: yup.number().required(i18n.t('jt_notification_storyboard_required.text')),
});

const loadStoryUsersRequest = async (inputValue: string) => {
    const response = await axios.get(window.URLS.userSearch, {
        params: {query: inputValue}
    }).catch((error) => {
        if (error.response?.status === 400) {
            return error.response;
        }
        throw error;
    });
    return response;
};

const loadStoryUsersRequestDebounced = AwesomeDebouncePromise(loadStoryUsersRequest, 280);

const NotificationsSettingsForm: React.FC<NotificationsSettingsFormProps> = React.memo((
    {
        notificationId,
        onDone,
    },
) => {
    const {showSuccess} = useAlert();
    const addOrEditNotification = useStoreActions((actions: any) => actions.notifications.addOrEditNotification);
    const setReloadNotifications = useStoreActions((actions: any) => actions.notifications.setReload);
    const notifications = useStoreState((state: any) => state.notifications.items);
    const [notificationsScheduleFieldChoices] = useData<ScheduleChoice[]>(window.URLS.notificationsScheduleFieldChoices, 'notificationsScheduleFieldChoices');

    const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
    const [userOptions, setUserOptions] = useState<UserOption[]>([]);
    const [configuredUsers, setConfiguredUsers] = useState<UserOption[]>([]);

    const initialValues: FormValues = {
        title: '',
        schedule: notificationsScheduleFieldChoices?.[0]?.value || '',
        storyboardId: 0,
        active: true,
    };

    useEffect(() => {
        if (notificationId && notifications) {
            const notification = notifications.find((n: any) => n.id === notificationId);
            if (notification?.usersConfigured) {
                const users = notification.usersConfigured.map((user: any) => ({
                    value: user.id,
                    label: `${user.identifier} (${user.email})`
                }));
                setConfiguredUsers(users);
            }
        }
    }, [notificationId, notifications]);

    const getUserOptions = async (inputValue: string): Promise<UserOption[]> => {
        const results = await loadStoryUsersRequestDebounced(inputValue);

        const selectAllOption: UserOption = {
            label: i18n.t('jt_st_select_all.text'),
            value: '*',
        };

        const userChoices: UserOption[] = results.data.map((user: User) => ({
            label: `${user.identifier} (${user.email})`,
            value: user.id,
        }));
        setUserOptions(userChoices);

        return [
            ...(inputValue || !userChoices.length ? [] : [selectAllOption]),
            ...userChoices,
        ];
    };

    const onUserSelection = (value: MultiValue<UserOption>) => {
        if (value && value.length && value[value.length - 1].value === '*') {
            setSelectedUsers(userOptions);
        } else {
            setSelectedUsers(value as UserOption[] || []);
        }
    };

    const addUsers = async () => {
        if (!notificationId || selectedUsers.length === 0) {return;}

        try {
            await axios.post(buildNotificationURL('configureNotification', notificationId), {
                users: selectedUsers
            });

            setConfiguredUsers(prev => [...prev, ...selectedUsers]);
            setSelectedUsers([]);
            showSuccess(i18n.t('jt_users_added_successfully.text'));
            setReloadNotifications(true);
        } catch (error) {
            console.error('Failed to add users:', error);
        }
    };

    const removeUsers = async (usersToRemove: UserOption[]) => {
        if (!notificationId || usersToRemove.length === 0) {return;}

        try {
            const userIds = usersToRemove.map(user => user.value as number);
            await axios.post(buildNotificationURL('removeNotificationPermBulk', notificationId), {
                userIds
            });

            setConfiguredUsers(prev =>
                prev.filter(user => !userIds.includes(user.value as number))
            );
            showSuccess(i18n.t('jt_users_removed_successfully.text'));
            setReloadNotifications(true);
        } catch (error) {
            console.error('Failed to remove users:', error);
        }
    };

    const customSuccessHandling = (response: any, actionName: string) => {
        if (notificationId) {
            showSuccess(i18n.t('jt_notification_updated_successfully.text'));
        } else {
            showSuccess(i18n.t('jt_notification_created_successfully.text'));
        }

        onDone();
        addOrEditNotification(response.data);
        setReloadNotifications(true);
    };

    return (
        <BaseSettingsForm
            entityId={notificationId || undefined}
            resourceUrl={window.URLS.notificationsResource}
            storeActionPath="notifications.addOrEditNotification"
            validationSchema={notificationSchema}
            initialValues={initialValues}
            successMessageKey="jt_save.text"
            customSuccessHandling={customSuccessHandling}
        >
            {(formik, loading) => (
                <>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="title" >
                                <Form.Label>
                                    {i18n.t('jt_title.text')}
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formik.values.title}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    name="title"
                                    isInvalid={formik.touched.title && !!formik.errors.title}
                                    autoFocus
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors.title}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="storyboardId" >
                                <Form.Label>
                                    {i18n.t('jt_select_the_storyboard.text')}
                                </Form.Label>
                                <SearchSelect
                                    optionsUrl={window.URLS.storyboards}
                                    formik={formik}
                                    fieldName="storyboardId"
                                    valueName="id"
                                    labelName="storyboardStoryInfo"
                                />
                                <Form.Control.Feedback
                                    type="invalid"
                                    style={{
                                        display: formik.touched.storyboardId && formik.errors.storyboardId ? 'block' : 'none',
                                    }}
                                >
                                    {formik.errors.storyboardId}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="schedule" >
                                <Form.Label>
                                    {i18n.t('jt_schedule.text')}
                                </Form.Label>
                                <SelectField
                                    name="schedule"
                                    value={notificationsScheduleFieldChoices?.find((x) => x.value === formik.values.schedule) || null}
                                    onChange={(value) => formik.setFieldValue('schedule', value?.value || '')}
                                    options={notificationsScheduleFieldChoices || []}
                                    isRequired={true}
                                    error={formik.errors.schedule}
                                    touched={formik.touched.schedule}
                                />
                            </Form.Group>
                        </Col>
                    </Row >

                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" >
                                <Form.Check
                                    name="active"
                                    label={i18n.t('es_active.text')}
                                    type="switch"
                                    onChange={formik.handleChange}
                                    checked={formik.values.active}
                                    isInvalid={!!formik.errors.active}
                                    feedback={formik.errors.active}
                                    id="notificationActive"
                                />
                            </Form.Group>
                        </Col>
                    </Row >

                    {notificationId && (
                        <Row className="mt-4">
                            <Col xs={12}>
                                <Card>
                                    <Card.Header>
                                        <h5>{i18n.t('jt_notification_users.text')}</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="mb-3">
                                            <Col xs={10}>
                                                <Form.Label>{i18n.t('jt_add_users.text')}</Form.Label>
                                                <AsyncSelect
                                                    isMulti
                                                    cacheOptions
                                                    defaultOptions
                                                    loadOptions={getUserOptions}
                                                    hideSelectedOptions={false}
                                                    onChange={onUserSelection}
                                                    value={selectedUsers}
                                                    placeholder={i18n.t('jt_search_users.text')}
                                                    styles={{
                                                        valueContainer: (provided) => ({
                                                            ...provided,
                                                            maxHeight: '100px',
                                                            overflowY: 'scroll',
                                                            scrollbarWidth: 'none',
                                                            overflowStyle: 'none',
                                                            '&::-webkit-scrollbar': {
                                                                width: 0,
                                                                height: 0,
                                                            },
                                                        }),
                                                    }}
                                                />
                                            </Col>
                                            <Col xs={2} className="d-flex align-items-end">
                                                <Button
                                                    variant="primary"
                                                    onClick={addUsers}
                                                    disabled={selectedUsers.length === 0}
                                                    size="sm"
                                                >
                                                    {i18n.t('jt_add.text')}
                                                </Button>
                                            </Col>
                                        </Row>

                                        {configuredUsers.length > 0 && (
                                            <Row>
                                                <Col xs={12}>
                                                    <Form.Label>{i18n.t('jt_configured_users.text')}</Form.Label>
                                                    <div className="border rounded p-2" style={{maxHeight: '150px', overflowY: 'auto'}}>
                                                        {configuredUsers.map((user) => (
                                                            <div key={user.value} className="d-flex justify-content-between align-items-center mb-1">
                                                                <span>{user.label}</span>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => removeUsers([user])}
                                                                >
                                                                    {i18n.t('jt_remove.text')}
                                                                </Button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {configuredUsers.length > 1 && (
                                                        <div className="mt-2">
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() => removeUsers(configuredUsers)}
                                                            >
                                                                {i18n.t('jt_remove_all_users.text')}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    <Row className="mt-4">
                        <Col xs={12} className="d-flex justify-content-end">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={formik.isSubmitting}
                                loading={formik.isSubmitting}
                                data-cy="notification-settings-save-btn"
                            >
                                {i18n.t('jt_save.text')}
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
        </BaseSettingsForm >
    );
});

NotificationsSettingsForm.displayName = 'NotificationsSettingsForm';

export default NotificationsSettingsForm; 